import "./PreviousNextButtonHeader.scss";
import leftChevron from "../../assets/previous-button-chevron.svg";
import rightChevron from "../../assets/right-chevron-lg.svg";

import { NavigationSteps } from "../../tp-core-types/NavigationSteps";
import { BuildNavigationStepDictionary } from "../navigation-header/NavigationStepDictionary";
import UserInfoContext from "../../tp-core-types/UserInfoModel";
import { MouseEvent, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useError } from "../../utilities/context/useError";
import FeatureFlagContext from "../../tp-core-types/FeatureFlagsModel";
import TPCoreAPI from "../../tp-core-api/TPCoreAPI";

export type Props = {
    selectedStep: NavigationSteps;
    isFormComplete: boolean;
    callbackOnSave: () => Promise<any>;
    stepsCompleted: string[] | null | undefined;
};

export default function PreviousNextButtonHeader({ selectedStep, isFormComplete, callbackOnSave, stepsCompleted }: Props) {
    const ff = useContext(FeatureFlagContext);
    const userContext = useContext(UserInfoContext);
    const navigate = useNavigate();
    const { setError } = useError();

    const navigationStepDictionary = BuildNavigationStepDictionary(
        ff.featureFlag.featureFlag_HcpApplication_SkillsChecklist_IsEnabled,
        ff?.featureFlag?.featureFlag_Terms_First
    );

    const isMobile = window.matchMedia("(max-width: 800px)").matches;
    const [previousStepText, setPreviousStepText] = useState<string | null>(null);
    const [nextStepText, setNextStepText] = useState<string | null>(null);
    const [isNextEnabled, setIsNextEnabled] = useState(false);

    useEffect(() => {
        const previous = navigationStepDictionary[selectedStep]?.previous;
        setPreviousStepText(previous ? navigationStepDictionary[previous]?.displayName : null);

        const next = navigationStepDictionary[selectedStep]?.next;
        setNextStepText(next ? navigationStepDictionary[next]?.displayName : null);

        setIsNextEnabled(isFormComplete);
    }, [selectedStep, isFormComplete, navigationStepDictionary]);

    const navigateToPrevious = () => {
        const previousStep = navigationStepDictionary[selectedStep]?.previous;
        if (previousStep) {
            navigate(navigationStepDictionary[previousStep]?.path);
        }
    };

    const handleNext = async () => {
        try {
            await callbackOnSave();
            const nextStep = navigationStepDictionary[selectedStep]?.next;
            if (nextStep) {
                navigate(navigationStepDictionary[nextStep]?.path);
            }
        } catch {
            setError(new Error("It looks like something went wrong. Please try again."));
        }
    };

    const handleSubmit = async (e: MouseEvent<HTMLButtonElement>) => {
        e.currentTarget.disabled = true;
        try {
            await callbackOnSave();
            await TPCoreAPI.saveSubmitApplication();
            userContext.refreshUserInfo();
            navigate("/dashboard");
        } catch {
            setIsNextEnabled(true);
            setError(new Error("It looks like something went wrong. Please try again."));
        }
    };

    const renderPreviousButton = () => {
        if (!previousStepText) return <div className="previous-button-spacer"></div>;

        return (
            <button
                className="previous-button"
                onClick={navigateToPrevious}
                data-testid="previous-button"
            >
                <img src={leftChevron} className="left-chevron" alt="previous-button" />
                {isMobile ? (
                    <div className="mobile-text-container">
                        <span className="bolded">Previous: </span>
                        <span>{previousStepText}</span>
                    </div>
                ) : (
                    "Previous"
                )}
            </button>
        );
    };

    const renderNextButton = () => {
        console.log("renderNextButton called", nextStepText);
        //if (!nextStepText) return null;

        const buttonText = stepsCompleted?.includes("Application Documents") && selectedStep === NavigationSteps.Identification
            ? "Submit Application"
            : isMobile
            ? (
                <div className="mobile-text-container">
                    <span className="bolded">Next: </span>
                    <span>{nextStepText}</span>
                </div>
            )
            : "Save and Proceed";

        const onClickHandler = stepsCompleted?.includes("Application Documents") && selectedStep === NavigationSteps.Identification
            ? handleSubmit
            : handleNext;

        return (
            <button
                className="save-and-proceed-button"
                onClick={onClickHandler}
                data-testid="save-and-proceed-button"
                disabled={!isNextEnabled}
            >
                <span>{buttonText}</span>
                {isNextEnabled && <img className="next-button-chevron" data-testid="right-chevron" src={rightChevron} alt="" />}
            </button>
        );
    };

    return (
        <div id="PreviousNextButtonHeader">
            <div className="buttons-container">
                {renderPreviousButton()}
                {renderNextButton()}
            </div>
        </div>
    );
}
